import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ChannelLogo } from '../components/channel-logo';
import { ChevronIcon, GlobeIcon } from '../components/icons';
import { useArticleByIdQuery } from '../hooks/use-feed-query';
import { useProfileQuery } from '../hooks/use-profile-query';
import { getLanguageName } from '../config/reference-data';

export function Article() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, isLoading, isError } = useArticleByIdQuery(id ?? null);
  const profile = useProfileQuery();

  if (isLoading) {
    return (
      <div className="nw-reader">
        <div className="nw-feed__loading">
          <div className="nw-feed__loading-spinner" aria-hidden />
          {t('web.article.loading')}
        </div>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="nw-reader">
        <button type="button" className="nw-reader__back" onClick={() => navigate(-1)}>
          <ChevronIcon dir="left" size={14} stroke={2.2} />
          {t('web.article.back')}
        </button>
        <div className="nw-feed__empty">{t('web.article.error')}</div>
      </div>
    );
  }

  const article = data;
  const time = formatDate(article.publishedAt);
  const fallbackEnglish = t('common.fallbacks.english');

  return (
    <div className="nw-reader">
      <button type="button" className="nw-reader__back" onClick={() => navigate(-1)}>
        <ChevronIcon dir="left" size={14} stroke={2.2} />
        {t('web.article.backToFeed')}
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <ChannelLogo name={article.channelName} logoUrl={article.channelLogo} size={32} />
        <div className="nw-reader__source">{article.channelName}</div>
        {article.isTranslated && article.translatedFrom && (
          <span className="nw-pill--translated">
            <GlobeIcon size={12} stroke={2} />
            {t('web.articleCard.translatedBadge', {
              source: getLanguageName(article.translatedFrom),
              target: getLanguageName(profile.data?.primaryLanguage ?? article.language) || fallbackEnglish,
            })}
          </span>
        )}
      </div>
      <h1 className="nw-reader__title">{article.title}</h1>
      <div className="nw-reader__meta">
        {time && <span>{time}</span>}
        {article.country && (
          <>
            <span>·</span>
            <span>{article.country}</span>
          </>
        )}
        {article.tags?.length > 0 && (
          <>
            <span>·</span>
            <span>{article.tags.slice(0, 3).map((t) => `#${t}`).join(' ')}</span>
          </>
        )}
      </div>
      {article.imageUrl && (
        <div className="nw-reader__hero">
          <img src={article.imageUrl} alt="" />
        </div>
      )}
      {article.summaryBullets?.length > 0 && (
        <div className="nw-reader__summary">
          <div className="nw-reader__summary-label">{t('web.article.summaryLabel')}</div>
          <ul className="nw-article__bullets">
            {article.summaryBullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </div>
      )}
      {article.summary && (
        <div className="nw-reader__body">
          {article.summary.split(/\n{2,}/).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      )}
      <div className="nw-reader__cta">
        <a className="nw-btn nw-btn--primary" href={article.sourceUrl} target="_blank" rel="noreferrer">
          {t('web.article.readOn', { channel: article.channelName })}
        </a>
      </div>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
